/**
 * Per-tournament, per-match GenAI use-case toggle grid.
 *
 * Layout:
 *   - Top: competition picker (search-as-you-type)
 *   - Below: a table of matches in that competition with one column per
 *     use-case (one-liner, hook questions, player moments, feed summary).
 *     Each cell is a 3-state toggle: ON / OFF / Default (follows global).
 *
 * Backed by:
 *   GET /admin/genai/tournaments/:cid/matches
 *   PATCH /admin/genai/matches/:matchId/config
 */
import React, { useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import {
  Alert, Autocomplete, Box, Card, CardContent, Chip, CircularProgress,
  IconButton, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchCompetitions,
  fetchTournamentMatches,
  patchMatchConfig,
  fetchCompetitionConfig,
  patchCompetitionConfig,
} from '../../services/genaiAdminApi';

// 3-state cell: ON / OFF / Default (null).
function TristateCell({ value, onChange, saving }) {
  // Click cycles: null → true → false → null
  const next = () => {
    if (value === null || value === undefined) onChange(true);
    else if (value === true) onChange(false);
    else onChange(null);
  };
  let label, color;
  if (value === true) { label = 'ON'; color = 'success'; }
  else if (value === false) { label = 'OFF'; color = 'error'; }
  else { label = 'Default'; color = 'default'; }
  return (
    <Tooltip title={value === null || value === undefined ? 'Follows global setting — click to override' : 'Click to cycle ON → OFF → Default'}>
      <span>
        <Chip
          size="small"
          label={saving ? '…' : label}
          color={color}
          variant={value === null || value === undefined ? 'outlined' : 'filled'}
          onClick={saving ? undefined : next}
          sx={{ minWidth: 80, cursor: saving ? 'not-allowed' : 'pointer' }}
        />
      </span>
    </Tooltip>
  );
}

const STATUS_LABEL = { 1: 'Scheduled', 2: 'Completed', 3: 'Live', 4: 'Abandoned' };

function GenAiMatchControls() {
  const [comps, setComps] = useState([]);
  const [compsLoading, setCompsLoading] = useState(true);
  const [selectedComp, setSelectedComp] = useState(null);
  const [compConfig, setCompConfig] = useState(null);   // tournament-level row
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Track which (matchId, field) is currently saving so we can disable that one cell.
  const [savingCell, setSavingCell] = useState(null);

  useEffect(() => {
    (async () => {
      setCompsLoading(true);
      try {
        const data = await fetchCompetitions({ limit: 500 });
        const list = Array.isArray(data?.competitions) ? data.competitions
          : Array.isArray(data) ? data
          : [];
        setComps(list);
      } catch (err) {
        setError(err?.message || 'Failed to load competitions');
      } finally {
        setCompsLoading(false);
      }
    })();
  }, []);

  const loadMatches = async (cid) => {
    if (!cid) return;
    setMatchesLoading(true);
    setError('');
    setSuccess('');
    try {
      // Load tournament-level config + match list in parallel.
      const [data, cfg] = await Promise.all([
        fetchTournamentMatches(cid),
        fetchCompetitionConfig(cid).catch(() => ({ config: null })),
      ]);
      setMatches(Array.isArray(data?.matches) ? data.matches : []);
      setCompConfig(cfg?.config ?? null);
    } catch (err) {
      setError(err?.message || 'Failed to load matches');
      setMatches([]);
      setCompConfig(null);
    } finally {
      setMatchesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedComp?.cid) loadMatches(selectedComp.cid);
    else { setMatches([]); setCompConfig(null); }
  }, [selectedComp]);

  const updateCompFlag = async (field, value) => {
    if (!selectedComp?.cid) return;
    setSavingCell(`comp:${field}`);
    setError(''); setSuccess('');
    try {
      const data = await patchCompetitionConfig(selectedComp.cid, { [field]: value });
      setCompConfig(data?.config ?? null);
      setSuccess(`Tournament default updated: ${field}`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to update tournament default');
    } finally {
      setSavingCell(null);
    }
  };

  const updateFlag = async (matchId, field, value) => {
    setSavingCell(`${matchId}:${field}`);
    setError('');
    setSuccess('');
    try {
      const patch = { [field]: value };
      const data = await patchMatchConfig(matchId, patch);
      // Refresh just this row locally — avoids reloading the whole table.
      setMatches(prev => prev.map(m => m.match_id === matchId ? { ...m, [field]: data?.config?.[field] ?? value } : m));
      setSuccess(`Updated ${field} for match ${matchId}`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSavingCell(null);
    }
  };

  // Sort: live → upcoming → recently completed → older completed.
  const sortedMatches = useMemo(() => {
    return [...matches].sort((a, b) => {
      const order = (s) => (s === 3 ? 0 : s === 1 ? 1 : s === 2 ? 2 : 3);
      const ao = order(a.status), bo = order(b.status);
      if (ao !== bo) return ao - bo;
      const ad = new Date(a.date_start || 0).getTime();
      const bd = new Date(b.date_start || 0).getTime();
      return bd - ad;
    });
  }, [matches]);

  return (
    <Component>
      <div className="page-body"><div className="container-fluid">
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>GenAI Match Controls</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Per-match per-use-case toggles. <strong>Default</strong> follows the global setting on the GenAI Control page.
          Use <strong>ON/OFF</strong> to override for individual matches (e.g. spend control on low-priority fixtures).
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Autocomplete
                sx={{ minWidth: 360, flex: 1 }}
                options={comps}
                loading={compsLoading}
                value={selectedComp}
                onChange={(_e, v) => setSelectedComp(v)}
                getOptionLabel={(opt) => opt?.title ? `${opt.title} (cid: ${opt.cid})` : ''}
                isOptionEqualToValue={(a, b) => a?.cid === b?.cid}
                renderInput={(params) => (
                  <TextField {...params} label="Tournament / Competition" placeholder="Search by name" size="small" />
                )}
              />
              <IconButton onClick={() => selectedComp?.cid && loadMatches(selectedComp.cid)} disabled={!selectedComp || matchesLoading}>
                <RefreshIcon />
              </IconButton>
              {selectedComp?.cid && (
                <Typography variant="body2" color="text.secondary">
                  {matches.length} match{matches.length === 1 ? '' : 'es'}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {!selectedComp ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Pick a tournament to see its matches.</Typography>
          </Paper>
        ) : matchesLoading ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Paper>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Match</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>One-liner</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Hook Q&amp;A</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Player Moments</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Feed Summary</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>AI Chat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Tournament-default row — applies to every match in this competition
                      unless a match has its own per-match override. */}
                  <TableRow sx={{ background: (theme) => theme.palette.action.hover }}>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Tournament default — applies to all matches below
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        cid: {selectedComp.cid} · per-match cells can still override these
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TristateCell
                        value={compConfig?.one_liner_enabled}
                        saving={savingCell === 'comp:one_liner_enabled'}
                        onChange={(v) => updateCompFlag('one_liner_enabled', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TristateCell
                        value={compConfig?.hook_questions_enabled}
                        saving={savingCell === 'comp:hook_questions_enabled'}
                        onChange={(v) => updateCompFlag('hook_questions_enabled', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TristateCell
                        value={compConfig?.player_moments_enabled}
                        saving={savingCell === 'comp:player_moments_enabled'}
                        onChange={(v) => updateCompFlag('player_moments_enabled', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TristateCell
                        value={compConfig?.feed_summary_enabled}
                        saving={savingCell === 'comp:feed_summary_enabled'}
                        onChange={(v) => updateCompFlag('feed_summary_enabled', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TristateCell
                        value={compConfig?.ai_chat_enabled}
                        saving={savingCell === 'comp:ai_chat_enabled'}
                        onChange={(v) => updateCompFlag('ai_chat_enabled', v)}
                      />
                    </TableCell>
                  </TableRow>
                  {sortedMatches.map(m => (
                    <TableRow key={m.match_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.title || `Match ${m.match_id}`}</Typography>
                        <Typography variant="caption" color="text.secondary">id: {m.match_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[m.status] || m.status || '—'}
                          color={m.status === 3 ? 'success' : m.status === 1 ? 'info' : 'default'}
                          variant={m.status === 3 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{m.date_start ? new Date(m.date_start).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</TableCell>
                      <TableCell align="center">
                        <TristateCell
                          value={m.one_liner_enabled}
                          saving={savingCell === `${m.match_id}:one_liner_enabled`}
                          onChange={(v) => updateFlag(m.match_id, 'one_liner_enabled', v)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TristateCell
                          value={m.hook_questions_enabled}
                          saving={savingCell === `${m.match_id}:hook_questions_enabled`}
                          onChange={(v) => updateFlag(m.match_id, 'hook_questions_enabled', v)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TristateCell
                          value={m.player_moments_enabled}
                          saving={savingCell === `${m.match_id}:player_moments_enabled`}
                          onChange={(v) => updateFlag(m.match_id, 'player_moments_enabled', v)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TristateCell
                          value={m.feed_summary_enabled}
                          saving={savingCell === `${m.match_id}:feed_summary_enabled`}
                          onChange={(v) => updateFlag(m.match_id, 'feed_summary_enabled', v)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TristateCell
                          value={m.ai_chat_enabled}
                          saving={savingCell === `${m.match_id}:ai_chat_enabled`}
                          onChange={(v) => updateFlag(m.match_id, 'ai_chat_enabled', v)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedMatches.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No matches in this tournament.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
      </div></div>
    </Component>
  );
}

export default GenAiMatchControls;
